/**
 * Wizard core (pure, React-free): a WizardAnswers object in, a valid Plan out.
 * The UI is a thin shell over this — all plan-building logic lives here where
 * it can be unit-tested like the engine, and can never drift from the model.
 *
 * Simple tier fills the core answers; the advanced tier adds gated modules.
 * Every builder tolerates missing sections (skipped steps) gracefully.
 */
import type { DebtType, Plan, StateCode } from '../model/types';
import { emptyPlan, CURRENT_YEAR } from '../model/defaults';
import { DEFAULT_ALLOCATION } from '../model/accountMeta';
import { DEFAULT_HEALTHCARE } from '../engine/healthcare';
import { piaFromEarnings } from '../engine/ssBenefits';

// ── Answers ─────────────────────────────────────────────────────────────────

export interface PersonAnswers {
  name?: string; // optional display label — never required, stays on-device
  age?: number;
  retirementAge?: number;
  salary?: number;
  contribution?: number;
  matchPercentOfSalary?: number; // employer matches on the first X% of pay
  matchRate?: number; // at this rate (0.5 = 50 cents per dollar)
  ssMonthlyAtFRA?: number;
  ssEstimated?: boolean; // true when we estimated it from salary
  claimingAge?: number;

  /**
   * Balances owned by this person. Asked per person rather than split from a
   * household total, because whose name is on an account decides when RMDs
   * start, when the early-withdrawal penalty lifts, and what a survivor keeps.
   */
  pretax?: number;
  roth?: number;
  brokerage?: number;
  /** What was paid into the brokerage — sets the gain when it is sold. */
  brokerageBasis?: number;
  cash?: number;
  hsa?: number;
  /** T-bills, treasuries, a bond fund — the safe money held outside cash. */
  bonds?: number;
  /** Money added to a taxable brokerage each year, beyond retirement accounts. */
  taxableSavingsPerYear?: number;
  /** Money added to T-bills/bonds each year — what fills a safety bucket. */
  bondSavingsPerYear?: number;
}

/** Where a windfall lands. Resolved to real accounts by the builder. */
export type WindfallDestination = 'spend' | 'cash' | 'brokerage' | 'bonds' | 'bucket';

export const WINDFALL_DESTINATION_LABELS: Record<WindfallDestination, string> = {
  spend: 'Spend it / general cash',
  cash: 'Cash & savings',
  brokerage: 'Taxable brokerage',
  bonds: 'Bonds / treasuries',
  bucket: 'Safety bucket',
};

export interface WizardAnswers {
  you: PersonAnswers;
  hasSpouse?: boolean;
  spouse?: PersonAnswers;
  state?: StateCode;

  /**
   * Household savings totals. Superseded by the per-person fields above; kept
   * so wizard drafts saved before per-person entry existed still load, and so
   * a single filer can be described either way.
   */
  pretax?: number;
  roth?: number;
  brokerage?: number;
  cash?: number;
  hsa?: number;
  taxableSavingsPerYear?: number;

  /**
   * Share of invested balances held in stocks, 0-100. Applied to every
   * investment account the wizard creates; cash accounts stay cash. One
   * question rather than one per account — per-account control lives in the
   * Accounts tab.
   */
  equityPct?: number;

  // Spending
  annualSpending?: number;
  retirementSpendingPct?: number; // 90 = spend 90% of today's budget when retired
  essentialPct?: number; // share of spending that is must-spend

  // Home
  ownsHome?: boolean;
  homeValue?: number;
  homeCostBasis?: number;
  mortgageBalance?: number;
  mortgageRate?: number; // decimal
  mortgagePayment?: number; // monthly
  propertyTaxAnnual?: number;

  // Healthcare (asked only when retiring before 65)
  preMedicare?: 'aca' | 'override' | 'skip';
  preMedicareMonthly?: number;

  // ── Advanced modules (each undefined unless its gate was answered yes) ──
  pensions?: {
    owner: 'you' | 'spouse';
    annualAmount: number;
    startAge: number;
    colaRate?: number;
    survivorPercent?: number;
  }[];
  rentals?: {
    value: number;
    costBasis: number;
    grossRent: number;
    vacancyPct: number;
    expensesAnnual: number;
    propertyTaxAnnual?: number;
    insuranceAnnual?: number;
  }[];
  /** Loans other than the mortgage — cars, students, credit cards, HELOCs. */
  debts?: {
    kind: DebtType;
    balance: number;
    annualRate: number;
    monthlyPayment: number;
  }[];
  /** Money still arriving after the job ends: consulting, part-time, royalties. */
  retirementIncome?: {
    owner: 'you' | 'spouse';
    label: string;
    annualAmount: number;
    startAge: number;
    endAge: number;
  }[];
  windfalls?: {
    year: number;
    amount: number;
    /** 'bonus' is ordinary earned income — taxed like a paycheck, not a gain. */
    kind: 'inheritance' | 'sale' | 'bonus';
    saleBasis?: number;
    /** Where the remainder lands after any split below. */
    destination?: WindfallDestination;
    /** Send this much somewhere specific first — topping up a cash bucket, say. */
    splitAmount?: number;
    splitDestination?: WindfallDestination;
  }[];
  /** `toState` is deliberately optional: the wizard makes the user pick one
   *  rather than defaulting, because state tax is the whole point of the
   *  question and a wrong default silently flatters the plan. No state, no
   *  relocation. */
  move?: { year: number; toState?: StateCode };
  ltc?: { forYou?: boolean; forSpouse?: boolean; annualCostToday?: number; onsetAge?: number };
  strategies?: {
    rothConversions?: { bracket: number; startAtMedicare: boolean };
    bucket?: {
      targetToday: number;
      protectAca: boolean;
      /**
       * How it gets filled. 'existing' points the strategy at T-bills already
       * owned; 'event' routes a windfall into it; 'savings' builds it from the
       * yearly bond saving. Left unset, the bucket is simply assumed to exist.
       */
      fundedBy?: 'existing' | 'event' | 'savings';
      /** Index into `windfalls` when fundedBy is 'event'. */
      fundFromWindfallIndex?: number;
    };
    tlh?: { baselinePct: number };
  };
}

/** Plain names for the debt picker, and the label the plan carries afterwards. */
export const DEBT_LABELS: Record<DebtType, string> = {
  mortgage: 'Mortgage',
  heloc: 'Home equity line',
  auto: 'Car loan',
  student: 'Student loan',
  personal: 'Personal loan',
  credit_card: 'Credit card',
};

// ── Social Security estimator ───────────────────────────────────────────────

/**
 * Rough monthly benefit at FRA from a current salary: assumes a full 35-year
 * career near this (inflation-adjusted) level and runs the real bend-point
 * formula. Labeled an estimate wherever shown — ssa.gov has the real number.
 */
export function estimateSsMonthlyFromSalary(salary: number): number {
  if (salary <= 0) return 0;
  const history = Array.from({ length: 35 }, (_, i) => ({
    year: CURRENT_YEAR - 35 + i,
    earnings: Math.min(salary, 184_500), // 2026 wage base cap
  }));
  return Math.round(piaFromEarnings(history));
}

// ── Builders ────────────────────────────────────────────────────────────────

function splitTwo(total: number): [number, number] {
  const half = Math.round(total / 2);
  return [half, total - half];
}

/** Build a complete Plan from wizard answers. Pure; never throws on gaps. */
export function applyAnswers(a: WizardAnswers): Plan {
  const plan = emptyPlan();
  const you = plan.household.people[0]!;
  const yourAge = a.you.age ?? 50;
  if (a.you.name?.trim()) you.label = a.you.name.trim();
  const spouseLabel = a.spouse?.name?.trim() || 'Spouse';
  you.birthYear = CURRENT_YEAR - yourAge;
  you.retirementAge = a.you.retirementAge ?? 65;
  you.socialSecurity.claimingAge = a.you.claimingAge ?? 67;
  you.socialSecurity.estimatedMonthlyAtFRA =
    a.you.ssMonthlyAtFRA ??
    (a.you.ssEstimated ? estimateSsMonthlyFromSalary(a.you.salary ?? 0) : 0);

  if (a.state) plan.household.stateOfResidence = a.state;

  let spouseId: string | undefined;
  if (a.hasSpouse) {
    const sAge = a.spouse?.age ?? yourAge;
    spouseId = 'person_spouse';
    plan.household.people.push({
      id: spouseId,
      label: spouseLabel,
      birthYear: CURRENT_YEAR - sAge,
      birthMonth: 1,
      retirementAge: a.spouse?.retirementAge ?? 65,
      lifeExpectancyAge: 92,
      socialSecurity: {
        estimatedMonthlyAtFRA:
          a.spouse?.ssMonthlyAtFRA ??
          (a.spouse?.ssEstimated ? estimateSsMonthlyFromSalary(a.spouse?.salary ?? 0) : 0),
        claimingAge: a.spouse?.claimingAge ?? 67,
      },
    });
    plan.household.filingStatus = 'mfj';
  }

  // Work income
  const addSalary = (ownerId: string, salary: number, age: number, retAge: number, label: string) => {
    if (salary > 0 && age < retAge) {
      plan.incomes.push({
        id: `inc_${ownerId}`,
        ownerId,
        label,
        type: 'w2',
        annualAmount: salary,
        startAge: age,
        endAge: retAge,
        taxable: true,
        taxTreatment: 'ordinary',
      });
    }
  };
  addSalary(you.id, a.you.salary ?? 0, yourAge, you.retirementAge, 'Salary');
  if (spouseId && a.spouse) {
    addSalary(spouseId, a.spouse.salary ?? 0, a.spouse.age ?? yourAge,
      a.spouse.retirementAge ?? 65, `${spouseLabel} salary`);
  }

  // Savings. Balances are asked per person; whose name is on an account decides
  // RMD timing, when the early-withdrawal penalty lifts, and what a survivor
  // inherits, so guessing a split would quietly distort all three.
  const owners: { id: string; label: string; answers: PersonAnswers | undefined }[] = [
    { id: you.id, label: '', answers: a.you },
    ...(spouseId ? [{ id: spouseId, label: `${spouseLabel} `, answers: a.spouse }] : []),
  ];

  // Drafts saved before per-person entry existed only carry household totals;
  // fall back to an even split for those rather than losing the balances.
  const perPersonGiven = owners.some((o) =>
    [
      o.answers?.pretax, o.answers?.roth, o.answers?.brokerage,
      o.answers?.cash, o.answers?.hsa, o.answers?.bonds,
    ].some((v) => v != null),
  );
  const legacySplit = (total: number | undefined): number[] => {
    if (!total || total <= 0) return owners.map(() => 0);
    return owners.length === 2 ? splitTwo(total) : [total];
  };

  // One stated stock share drives every invested account; cash stays cash.
  const equityPct = Math.max(0, Math.min(100, a.equityPct ?? 60));
  const investedAllocation = {
    equityPct,
    bondPct: Math.round((100 - equityPct) * 0.85),
    cashPct: 100 - equityPct - Math.round((100 - equityPct) * 0.85),
  };

  const addAccounts = (
    type: Plan['accounts'][number]['type'],
    label: string,
    pick: (p: PersonAnswers | undefined) => number | undefined,
    legacyTotal: number | undefined,
    basis?: (p: PersonAnswers | undefined, balance: number) => number,
  ) => {
    const fallback = perPersonGiven ? null : legacySplit(legacyTotal);
    // A bond or cash account is what it says it is; only the diversified
    // accounts take the household stock/bond mix.
    const allocation = DEFAULT_ALLOCATION[type] ?? investedAllocation;
    owners.forEach((o, i) => {
      const balance = fallback ? fallback[i]! : (pick(o.answers) ?? 0);
      if (balance <= 0) return;
      plan.accounts.push({
        id: `acct_${type}_${i + 1}`,
        ownerId: o.id,
        type,
        label: `${o.label}${label}`,
        balance,
        asOfDate: `${CURRENT_YEAR}-01-01`,
        allocation: { ...allocation },
        ...(basis ? { costBasis: basis(o.answers, balance) } : {}),
      });
    });
  };
  addAccounts('trad_401k', '401(k)', (p) => p?.pretax, a.pretax);
  addAccounts('roth_ira', 'Roth IRA', (p) => p?.roth, a.roth);
  addAccounts('brokerage', 'Brokerage', (p) => p?.brokerage, a.brokerage,
    // Basis is asked outright; only estimate when the question was skipped.
    (p, balance) => p?.brokerageBasis ?? Math.round(balance * 0.7));
  addAccounts('checking_savings', 'Cash', (p) => p?.cash, a.cash, (_p, balance) => balance);
  addAccounts('hsa', 'HSA', (p) => p?.hsa, a.hsa);
  // T-bills and bonds get their own account so they earn the bond rate and can
  // serve as the safety bucket without being mixed into equities.
  addAccounts('bonds', 'T-bills / bonds', (p) => p?.bonds, undefined, (_p, balance) => balance);

  // Contributions + employer match (per person, against their own account).
  const wire = (ownerId: string, p: PersonAnswers | undefined) => {
    if (!p) return;
    const acct = plan.accounts.find((x) => x.type === 'trad_401k' && x.ownerId === ownerId);
    const target =
      acct ??
      ((p.contribution ?? 0) > 0
        ? plan.accounts[
            plan.accounts.push({
              id: `acct_401k_${ownerId}`,
              ownerId,
              type: 'trad_401k',
              label: ownerId === you.id ? '401(k)' : `${spouseLabel} 401(k)`,
              balance: 0,
            }) - 1
          ]
        : undefined);
    if (!target) return;
    target.contributions = {
      employee: p.contribution ?? 0,
      ...(p.matchPercentOfSalary && p.matchRate
        ? {
            employerMatch: {
              percentOfSalary: p.matchPercentOfSalary,
              matchRate: p.matchRate,
              cap: 100_000,
            },
          }
        : {}),
    };
  };
  wire(you.id, a.you);
  if (spouseId) wire(spouseId, a.spouse);

  // Taxable saving, per person, into that person's own brokerage.
  const wireTaxableSaving = (o: { id: string; label: string; answers: PersonAnswers | undefined }) => {
    const amount = o.answers?.taxableSavingsPerYear ?? 0;
    if (amount <= 0) return;
    let target = plan.accounts.find((x) => x.type === 'brokerage' && x.ownerId === o.id);
    if (!target) {
      target = {
        id: `acct_brokerage_new_${o.id}`,
        ownerId: o.id,
        type: 'brokerage',
        label: `${o.label}Brokerage`,
        balance: 0,
        costBasis: 0,
        allocation: { ...investedAllocation },
      };
      plan.accounts.push(target);
    }
    target.contributions = { ...(target.contributions ?? {}), employee: amount };
  };
  owners.forEach(wireTaxableSaving);

  // Yearly saving into T-bills/bonds — the usual way a safety bucket is built.
  const wireBondSaving = (o: { id: string; label: string; answers: PersonAnswers | undefined }) => {
    const amount = o.answers?.bondSavingsPerYear ?? 0;
    if (amount <= 0) return;
    let target = plan.accounts.find((x) => x.type === 'bonds' && x.ownerId === o.id);
    if (!target) {
      target = {
        id: `acct_bonds_new_${o.id}`,
        ownerId: o.id,
        type: 'bonds',
        label: `${o.label}T-bills / bonds`,
        balance: 0,
        costBasis: 0,
        allocation: { equityPct: 0, bondPct: 100, cashPct: 0 },
      };
      plan.accounts.push(target);
    }
    target.contributions = { ...(target.contributions ?? {}), employee: amount };
  };
  owners.forEach(wireBondSaving);

  // Older drafts carried a single household figure; keep honouring it.
  const perPersonSaving = owners.some((o) => (o.answers?.taxableSavingsPerYear ?? 0) > 0);
  if (!perPersonSaving && (a.taxableSavingsPerYear ?? 0) > 0) {
    const b = plan.accounts.find((x) => x.type === 'brokerage');
    if (b) b.contributions = { ...(b.contributions ?? {}), employee: a.taxableSavingsPerYear! };
    else {
      plan.accounts.push({
        id: 'acct_brokerage_new',
        ownerId: you.id,
        type: 'brokerage',
        label: 'Brokerage',
        balance: 0,
        costBasis: 0,
        allocation: { ...investedAllocation },
        contributions: { employee: a.taxableSavingsPerYear! },
      });
    }
  }

  // Spending
  plan.budget = {
    mode: 'simple',
    annualAmount: a.annualSpending ?? 0,
    retirementSpendingMultiplier: (a.retirementSpendingPct ?? 100) / 100,
    essentialFraction: (a.essentialPct ?? 65) / 100,
  };

  // Home + mortgage
  if (a.ownsHome && (a.homeValue ?? 0) > 0) {
    const mortgageId = (a.mortgageBalance ?? 0) > 0 ? 'debt_mortgage' : undefined;
    plan.properties.push({
      id: 'prop_home',
      label: 'Home',
      type: 'primary',
      ownership: mortgageId ? 'own_with_mortgage' : 'own_outright',
      currentValue: a.homeValue!,
      costBasis: a.homeCostBasis ?? Math.round(a.homeValue! * 0.6),
      sellingCostPct: 0.07,
      mortgageId,
      propertyTaxAnnual: a.propertyTaxAnnual ?? 0,
      insuranceAnnual: 0,
      hoaAnnual: 0,
      maintenancePctOfValue: 0.01,
    });
    if (mortgageId) {
      plan.debts.push({
        id: mortgageId,
        label: 'Mortgage',
        type: 'mortgage',
        balance: a.mortgageBalance!,
        annualRate: a.mortgageRate ?? 0.06,
        monthlyPayment: a.mortgagePayment ?? 0,
      });
    }
  }

  // Healthcare: defaults unless the pre-65 question chose an override.
  plan.healthcare = { ...DEFAULT_HEALTHCARE };
  if (a.preMedicare === 'override') {
    plan.healthcare.preMedicareMode = 'override';
    plan.healthcare.overrideMonthlyPremium = a.preMedicareMonthly ?? 0;
  } else if (a.preMedicare === 'aca' && (a.preMedicareMonthly ?? 0) > 0) {
    plan.healthcare.benchmarkMonthlyPremium = a.preMedicareMonthly!;
  }

  applyAdvanced(plan, a, you.id, spouseId);
  return plan;
}

// ── Advanced modules ────────────────────────────────────────────────────────

function applyAdvanced(
  plan: Plan,
  a: WizardAnswers,
  youId: string,
  spouseId: string | undefined,
): void {
  // Untouched extra rows (all zeros) are skipped rather than added as $0 items.
  for (const [i, p] of (a.pensions ?? []).entries()) {
    if (p.annualAmount <= 0) continue;
    const ownerId = p.owner === 'spouse' && spouseId ? spouseId : youId;
    plan.incomes.push({
      id: `inc_pension_${i + 1}`,
      ownerId,
      label: 'Pension',
      type: 'pension_monthly',
      annualAmount: p.annualAmount,
      startAge: p.startAge,
      endAge: 120,
      colaRate: p.colaRate ?? 0,
      taxable: true,
      taxTreatment: 'ordinary',
      // The amount asked for is what the statement shows under their chosen
      // election, so no further reduction is applied here.
      ...(p.survivorPercent && spouseId
        ? { survivorElection: { percentToSurvivor: p.survivorPercent, benefitReduction: 0 } }
        : {}),
    });
  }

  for (const [i, r] of (a.rentals ?? []).entries()) {
    if (r.value <= 0 && r.grossRent <= 0) continue;
    plan.properties.push({
      id: `prop_rental_${i + 1}`,
      label: `Rental ${i + 1}`,
      type: 'rental',
      ownership: 'own_outright',
      currentValue: r.value,
      costBasis: r.costBasis,
      sellingCostPct: 0.07,
      propertyTaxAnnual: r.propertyTaxAnnual ?? 0,
      insuranceAnnual: r.insuranceAnnual ?? 0,
      hoaAnnual: 0,
      maintenancePctOfValue: 0.01,
      rentalIncome: {
        grossAnnual: r.grossRent,
        vacancyPct: r.vacancyPct,
        expensesAnnual: r.expensesAnnual,
      },
    });
  }

  for (const [i, d] of (a.debts ?? []).entries()) {
    if (d.balance <= 0) continue;
    plan.debts.push({
      id: `debt_${d.kind}_${i + 1}`,
      label: DEBT_LABELS[d.kind],
      type: d.kind,
      balance: d.balance,
      annualRate: d.annualRate,
      monthlyPayment: d.monthlyPayment,
    });
  }

  for (const [i, inc] of (a.retirementIncome ?? []).entries()) {
    if (inc.annualAmount <= 0) continue;
    const ownerId = inc.owner === 'spouse' && spouseId ? spouseId : youId;
    plan.incomes.push({
      id: `inc_later_${i + 1}`,
      ownerId,
      label: inc.label || 'Work in retirement',
      type: 'self_employment',
      annualAmount: inc.annualAmount,
      startAge: inc.startAge,
      endAge: inc.endAge,
      taxable: true,
      taxTreatment: 'ordinary',
    });
  }


  if (a.move?.toState) {
    plan.household.relocations.push({ year: a.move.year, toState: a.move.toState });
  }

  if (a.ltc?.forYou || a.ltc?.forSpouse) {
    plan.ltcEvents = [];
    const add = (personId: string) =>
      plan.ltcEvents!.push({
        personId,
        enabled: true,
        onsetAge: a.ltc?.onsetAge ?? 85,
        durationYears: 3,
        annualCostToday: a.ltc?.annualCostToday ?? 110_000,
        careType: 'assisted_living',
      });
    if (a.ltc.forYou) add(youId);
    if (a.ltc.forSpouse && spouseId) add(spouseId);
  }

  // Set when a bucket is to be filled from a specific windfall; applied when
  // the events are written, below.
  let bucketFunding: { windfallIndex: number; accountId: string; amount: number } | undefined;

  const s = a.strategies;
  if (!s) {
    addWindfalls(plan, a, youId);
    return;
  }

  if (s.rothConversions) {
    const people = plan.household.people;
    const retirementYear = Math.min(...people.map((p) => p.birthYear + p.retirementAge));
    const medicareYear = Math.min(...people.map((p) => p.birthYear + 65));
    const rmdYearMinus1 = Math.min(...people.map((p) => p.birthYear + 74));
    const startYear = Math.max(
      CURRENT_YEAR,
      s.rothConversions.startAtMedicare ? medicareYear : retirementYear,
    );
    plan.decisions.rothConversionRule = {
      type: 'bracket',
      target: s.rothConversions.bracket,
      startYear,
      endYear: Math.max(startYear, rmdYearMinus1),
    };
    // Conversions need somewhere to land.
    if (!plan.accounts.some((x) => x.type.startsWith('roth'))) {
      plan.accounts.push({
        id: 'acct_roth_for_conversions',
        ownerId: youId,
        type: 'roth_ira',
        label: 'Roth IRA (for conversions)',
        balance: 0,
      });
    }
  }

  if (s.bucket) {
    // Prefer money the household already holds for exactly this purpose:
    // T-bills first, then an existing money-market account. Inventing a second
    // empty cash pile beside real T-bills is the confusing outcome.
    let bucketAcct =
      plan.accounts.find((x) => x.type === 'bonds')
      ?? plan.accounts.find((x) => x.type === 'cd_money_market');
    if (!bucketAcct) {
      bucketAcct = {
        id: 'acct_bucket',
        ownerId: youId,
        type: 'cd_money_market',
        label: 'Safety bucket (T-bills)',
        balance: 0,
        costBasis: 0,
        allocation: { equityPct: 0, bondPct: 0, cashPct: 100 },
      };
      plan.accounts.push(bucketAcct);
    }
    plan.decisions.bucketStrategy = {
      accountId: bucketAcct.id,
      targetToday: s.bucket.targetToday,
      protectAcaYears: s.bucket.protectAca,
      windowYears: 10,
    };

    // Fill it from a nominated windfall: the split is written onto that event
    // so the money visibly moves, rather than the bucket just existing.
    if (s.bucket.fundedBy === 'event') {
      const idx = s.bucket.fundFromWindfallIndex ?? 0;
      const w = a.windfalls?.[idx];
      if (w && w.amount > 0) {
        bucketFunding = { windfallIndex: idx, accountId: bucketAcct.id, amount: s.bucket.targetToday };
      }
    }
  }

  if (s.tlh) {
    plan.decisions.taxLossHarvesting = {
      enabled: true,
      downYearCapture: 0.75,
      baselinePct: s.tlh.baselinePct,
    };
  }

  // Last, so a windfall can be routed into the safety bucket created above.
  addWindfalls(plan, a, youId, bucketFunding);
}

// ── Windfalls ───────────────────────────────────────────────────────────────

const WINDFALL_META = {
  inheritance: { label: 'Inheritance', taxTreatment: 'tax_free' as const },
  sale: { label: 'Sale proceeds', taxTreatment: 'ltcg' as const },
  bonus: { label: 'Bonus', taxTreatment: 'ordinary' as const },
};

/**
 * Find (or create) the account a chosen destination refers to. Returns
 * undefined for 'spend', which the engine reads as ordinary spendable cash.
 */
function resolveDestination(
  plan: Plan,
  youId: string,
  dest: WindfallDestination | undefined,
): string | undefined {
  if (!dest || dest === 'spend') return undefined;

  const findOrAdd = (
    type: Plan['accounts'][number]['type'],
    label: string,
    allocation: { equityPct: number; bondPct: number; cashPct: number },
  ): string => {
    const existing = plan.accounts.find((x) => x.type === type);
    if (existing) return existing.id;
    const id = `acct_${type}_windfall`;
    plan.accounts.push({
      id, ownerId: youId, type, label, balance: 0, costBasis: 0, allocation,
    });
    return id;
  };

  switch (dest) {
    case 'cash':
      return findOrAdd('checking_savings', 'Cash', { equityPct: 0, bondPct: 0, cashPct: 100 });
    case 'brokerage':
      return findOrAdd('brokerage', 'Brokerage', { equityPct: 60, bondPct: 35, cashPct: 5 });
    case 'bonds':
      return findOrAdd('bonds', 'Bonds / Treasuries', { equityPct: 0, bondPct: 100, cashPct: 0 });
    case 'bucket':
      return findOrAdd('cd_money_market', 'Safety bucket (T-bills)', { equityPct: 0, bondPct: 0, cashPct: 100 });
  }
}

function addWindfalls(
  plan: Plan,
  a: WizardAnswers,
  youId: string,
  bucketFunding?: { windfallIndex: number; accountId: string; amount: number },
): void {
  for (const [i, w] of (a.windfalls ?? []).entries()) {
    if (w.amount <= 0) continue;
    const meta = WINDFALL_META[w.kind] ?? WINDFALL_META.inheritance;

    // Splits stated on the windfall itself, plus a bucket top-up if this is
    // the event nominated to fill it. Amounts are gross; the engine reserves
    // the estimated tax before any of them are paid out.
    const splits: { accountId: string; amount: number }[] = [];
    if (bucketFunding?.windfallIndex === i && bucketFunding.amount > 0) {
      splits.push({ accountId: bucketFunding.accountId, amount: bucketFunding.amount });
    }
    const stated =
      (w.splitAmount ?? 0) > 0 ? resolveDestination(plan, youId, w.splitDestination) : undefined;
    if (stated && !splits.some((x) => x.accountId === stated)) {
      splits.push({ accountId: stated, amount: w.splitAmount! });
    }

    plan.oneTimeEvents.push({
      id: `ev_windfall_${i + 1}`,
      year: w.year,
      label: meta.label,
      amount: w.amount,
      taxTreatment: meta.taxTreatment,
      // Taxable proceeds: hold back the estimated bill so the amounts routed
      // to accounts are what actually survives.
      ...(meta.taxTreatment !== 'tax_free' ? { reserveTaxFromProceeds: true } : {}),
      ...(w.kind === 'sale' ? { costBasis: w.saleBasis ?? 0 } : {}),
      ...(splits.length ? { destinations: splits } : {}),
      ...(() => {
        const rest = resolveDestination(plan, youId, w.destination);
        return rest ? { destinationAccountId: rest } : {};
      })(),
    });
  }
}
